/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api-client';
import { ApiResponse } from '../interfaces';
import { getCountryName } from '@utils/countries';
import { getTechnologyProject } from '@utils/project-technologies';
import { NOT_APPLICABLE } from '@config/constants/common';
import { isNumber } from '@utils/functions';

export const getAdditionalProjects = (
	params: any
): Promise<ApiResponse> => {
	return apiClient.get('/additionalities', {
		params: {
			active: true,
			page: params.page,
			pageSize: params.pageSize,
			query: params.queryParams,
			sort: '-financing.unlimited -financing.remaining_euro name',
			populate:
				'cover, logo, publicSpecs, supportedBy.logo',
		},
	});
};

export const useAdditinalProjects = (
	page: number,
	size: number,
	queryParams: any
) => {
	const { data, isFetching, isFetched, isRefetching } =
		useQuery({
			queryKey: [
				'additionalities-projects-offsets',
				{
					page: page,
					size: size,
					queryParams: queryParams,
				},
			],
			queryFn: () =>
				getAdditionalProjects({
					page: page,
					pageSize: size,
					queryParams: queryParams,
				}),
			staleTime: 1000,
		});

	const updateList = (projects: any) =>
		projects.reduce((acc: any, currentItem: any) => {
			const technolgyProject = getTechnologyProject(
				currentItem.technology
			);
			const countryName = getCountryName(
				currentItem.location.countryCode
			);

			const unlimited = currentItem?.financing?.unlimited;
			const financialNeeds = unlimited
				? 'Unlimited'
				: currentItem?.financing?.target_euro;
			const coverage =
				currentItem.financing.pledged_euro.toString();

			const percent =
				currentItem.financing &&
				isNumber(currentItem.financing.target_euro) &&
				isNumber(currentItem.financing.remaining_euro)
					? ((currentItem.financing.target_euro -
							currentItem.financing.remaining_euro) /
							currentItem.financing.target_euro) *
						100
					: 0;

			const progress = unlimited
				? 40
				: Math.round(percent);

			const cover =
				currentItem && currentItem.cover
					? currentItem.cover
					: technolgyProject
						? technolgyProject.image
						: '';

			const modifiedItem = {
				id: currentItem.id,
				name: currentItem.name,
				country: {
					name: countryName,
					code: currentItem.location.countryCode,
				},
				region:
					currentItem.location &&
					currentItem.location.place
						? currentItem.location.place
						: NOT_APPLICABLE,
				type: technolgyProject
					? technolgyProject.label
					: '',
				image: cover,
				price: 0,
				financialNeeds: financialNeeds,
				coverage: coverage,
				remaining_euro: currentItem?.financing
					.remaining_euro
					? currentItem?.financing.remaining_euro
					: 0,
				progress: progress,
				supportedBy: currentItem?.supportedBy?.logo,
				isDisabled: currentItem?.financing?.unlimited
					? false
					: !currentItem?.financing?.remaining_euro,
			};
			acc.push(modifiedItem);

			return acc;
		}, []);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isRefetching,
		isFetching,
		isFetched,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
